import { useEffect } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import { AdminRoutes } from '@admin/AdminRoutes';
import { ProtectedRoute, PublicRoute, RouteErrorBoundary } from '@components';
import { Auth, ForgotPassword, ResetPassword } from '@features/auth';
import { BatchList } from '@features/batches';
import { CustomerList, CustomerView } from '@features/customers';
import { DeclarationGroupList, DeclarationList, DeclarationView } from '@features/declarations';
import { ForwarderList, ForwarderView } from '@features/forwarders';
import { Help } from '@features/help';
import { ImporterList, ImporterView } from '@features/importers';
import { UserLayout } from '@features/layout';
import { ManageAccount } from '@features/manage-account';
import { MongoDashboard } from '@features/mongo-dashboard';
import { TransitList, TransitView } from '@features/transits';
import { TSDList, TSDView } from '@features/tsd';
import { useLogger } from '@hooks/useLogger';
import { useRefreshToken } from '@hooks/user';
import { useSettings } from '@hooks/useSettings';

export const Navigation = () => {
  useRefreshToken();
  const { data: settings } = useSettings();
  const { init, initPerformance } = useLogger();

  const pathWithIdAndTabId = ':id/:tabId';

  const routes = [
    {
      path: '',
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          path: '/',
          element: (
            <ProtectedRoute>
              <UserLayout />
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: <Navigate to="/batches" replace /> },
            { path: 'batches', element: <BatchList /> },
            { path: 'batches/:tabId', element: <BatchList /> },
            { path: 'declaration-groups', element: <DeclarationGroupList /> },
            {
              path: 'declarations',
              children: [
                { index: true, element: <DeclarationList /> },
                { path: ':id', element: <DeclarationView /> },
                { path: pathWithIdAndTabId, element: <DeclarationView /> },
              ],
            },
            {
              path: 'transits',
              children: [
                { index: true, element: <TransitList /> },
                { path: ':id', element: <TransitView /> },
                { path: pathWithIdAndTabId, element: <TransitView /> },
              ],
            },
            {
              path: 'tsd',
              children: [
                { index: true, element: <TSDList /> },
                { path: ':id', element: <TSDView /> },
                { path: pathWithIdAndTabId, element: <TSDView /> },
              ],
            },
            {
              path: 'importers',
              children: [
                { index: true, element: <ImporterList /> },
                { path: ':id', element: <ImporterView /> },
                { path: pathWithIdAndTabId, element: <ImporterView /> },
              ],
            },
            {
              path: 'forwarders',
              children: [
                { index: true, element: <ForwarderList /> },
                { path: ':id', element: <ForwarderView /> },
                { path: pathWithIdAndTabId, element: <ForwarderView /> },
              ],
            },
            {
              path: 'customers',
              children: [
                { index: true, element: <CustomerList /> },
                { path: 'add', element: <CustomerView /> },
                { path: 'add/:tabId', element: <CustomerView /> },
                { path: ':id', element: <CustomerView /> },
                { path: pathWithIdAndTabId, element: <CustomerView /> },
                // todo: replace it with /batches?customerId={customerId}
                { path: 'batches', element: <BatchList /> },
                // todo: replace it with /declarations?customerId={customerId}
                { path: 'declarations', element: <DeclarationList /> },
              ],
            },
            { path: 'manage-account', element: <ManageAccount /> },
            { path: 'manage-account/:tabId', element: <ManageAccount /> },
            { path: 'mongo-dashboard', element: <MongoDashboard /> },
            { path: 'mongo-dashboard/:tabId', element: <MongoDashboard /> },
            { path: 'help', element: <Help /> },
          ],
        },
      ],
    },
    {
      element: <PublicRoute />,
      children: [
        { path: 'auth', element: <Auth /> },
        { path: 'forgot-password', element: <ForgotPassword /> },
        { path: 'reset-password', element: <ResetPassword /> },
      ],
    },
    ...AdminRoutes,
    { path: '*', element: <Navigate to="/" replace /> },
  ];

  const addErrorBoundaries = (routes) => {
    return routes.map((route) => {
      const newRoute = { ...route };
      if (newRoute.element && newRoute.element.type !== Navigate) {
        newRoute.errorElement = <RouteErrorBoundary />;
      }
      if (newRoute.children) {
        newRoute.children = addErrorBoundaries(newRoute.children);
      }

      return newRoute;
    });
  };

  const router = createBrowserRouter(addErrorBoundaries(routes));

  useEffect(() => {
    init();
    initPerformance(routes);
  }, [settings]);

  return <RouterProvider router={router} />;
};
